import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import { AuthProvider } from "@/contexts/AuthContext";
import ScrollToTop from "@/components/ScrollToTop";
import AnalyticsTracker from "@/components/AnalyticsTracker";

import { lazy, Suspense, type ComponentType } from "react";
import Index from "./pages/Index";

/**
 * Lazy import with one automatic retry + reload fallback.
 * Guards against stale chunk hashes after a new deploy, which otherwise
 * surface as "Failed to fetch dynamically imported module" + blank screen.
 */
const lazyRoute = <T extends { default: ComponentType<any> }>(
  factory: () => Promise<T>,
  name?: string
) =>
  lazy(() =>
    factory().catch(async (error) => {
      try {
        return await factory();
      } catch {
        const key = `chunk-reload:${name ?? "app"}`;
        const last = Number(sessionStorage.getItem(key) ?? 0);
        // Reload at most once per minute per chunk to pick up the new deploy.
        if (Date.now() - last > 60_000) {
          sessionStorage.setItem(key, String(Date.now()));
          window.location.reload();
          return new Promise<T>(() => {});
        }
        throw error;
      }
    })
  );


// Lazy-loaded routes
const CaseStudy = lazyRoute(() => import("./pages/CaseStudy"), "./pages/CaseStudy");
const LunoStudy = lazyRoute(() => import("./pages/LunoStudy"), "./pages/LunoStudy");
const SpaiStudy = lazyRoute(() => import("./pages/SpaiStudy"), "./pages/SpaiStudy");
const LeonardoStudy = lazyRoute(() => import("./pages/LeonardoStudy"), "./pages/LeonardoStudy");
const RekhtaStudy = lazyRoute(() => import("./pages/RekhtaStudy"), "./pages/RekhtaStudy");
const FutureFabricStudy = lazyRoute(() => import("./pages/FutureFabricStudy"), "./pages/FutureFabricStudy");

const Thinking = lazyRoute(() => import("./pages/Thinking"), "./pages/Thinking");
const Insight = lazyRoute(() => import("./pages/Insight"), "./pages/Insight");
const ContextEssay = lazyRoute(() => import("./pages/ContextEssay"), "./pages/ContextEssay");
const IterationEssay = lazyRoute(() => import("./pages/IterationEssay"), "./pages/IterationEssay");
const MetaverseEssay = lazyRoute(() => import("./pages/MetaverseEssay"), "./pages/MetaverseEssay");
const CoherenceEssay = lazyRoute(() => import("./pages/CoherenceEssay"), "./pages/CoherenceEssay");

const NotFound = lazyRoute(() => import("./pages/NotFound"), "./pages/NotFound");
const AdminLogin = lazyRoute(() => import("./pages/admin/Login"), "./pages/admin/Login");
const AdminLayout = lazyRoute(() => import("./pages/admin/AdminLayout"), "./pages/admin/AdminLayout");
const CaseStudies = lazyRoute(() => import("./pages/admin/CaseStudies"), "./pages/admin/CaseStudies");
const CaseStudyEditor = lazyRoute(() => import("./pages/admin/CaseStudyEditor"), "./pages/admin/CaseStudyEditor");
const Insights = lazyRoute(() => import("./pages/admin/Insights"), "./pages/admin/Insights");
const InsightEditor = lazyRoute(() => import("./pages/admin/InsightEditor"), "./pages/admin/InsightEditor");
const MediaLibrary = lazyRoute(() => import("./pages/admin/MediaLibrary"), "./pages/admin/MediaLibrary");
const AdminSettings = lazyRoute(() => import("./pages/admin/Settings"), "./pages/admin/Settings");
const OAuthConsent = lazyRoute(() => import("./pages/OAuthConsent"), "./pages/OAuthConsent");

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
      <TooltipProvider>
        <BrowserRouter>
          <AuthProvider>
            <ScrollToTop />
            <AnalyticsTracker />

            <Toaster />
            <Sonner />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Index />} />
                <Route path="/work/luno" element={<LunoStudy />} />
                <Route path="/work/spai" element={<SpaiStudy />} />
                <Route path="/work/leonardo" element={<LeonardoStudy />} />
                <Route path="/work/rekhta" element={<RekhtaStudy />} />
                <Route path="/work/future-fabric" element={<FutureFabricStudy />} />
                <Route path="/work/kaan-air" element={<NotFound />} />
                <Route path="/work/one8" element={<NotFound />} />
                
                <Route path="/work/:slug" element={<CaseStudy />} />
                <Route path="/thinking" element={<Thinking />} />
                <Route
                  path="/thinking/the-missing-layer-in-ai-native-creative-operations"
                  element={<ContextEssay />}
                />
                <Route
                  path="/thinking/the-infinite-iteration-problem"
                  element={<IterationEssay />}
                />
                <Route
                  path="/thinking/the-metaverse-wasnt-early-the-content-was"
                  element={<MetaverseEssay />}
                />
                <Route
                  path="/thinking/designing-coherent-products-with-ai-agents"
                  element={<CoherenceEssay />}
                />

                <Route path="/thinking/:slug" element={<Insight />} />
                <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                
                {/* Admin routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<CaseStudies />} />
                  <Route path="case-study/:id" element={<CaseStudyEditor />} />
                  <Route path="thinking" element={<Insights />} />
                  <Route path="thinking/:id" element={<InsightEditor />} />
                  <Route path="media" element={<MediaLibrary />} />
                  <Route path="settings" element={<AdminSettings />} />
                </Route>
                
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
